import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { PERSISTED_PATH_PREFIX } from '@/hooks/usePersistedPath';

const LOG_PREFIX = '[CanvasIframe]';

const logDebug = import.meta.env.DEV
  ? (...args: unknown[]) => console.warn(LOG_PREFIX, ...args)
  : () => {};

interface CanvasMessage {
  event?: string;
  action?: string;
  requestId?: string;
  data?: Record<string, unknown>;
}

interface UseCanvasIframeOptions {
  canvasOrigin: string;
  onReady: () => void;
}

/**
 * Lightweight postMessage bridge for the Canvas templates iframe.
 *
 * Handles two events from the Canvas app:
 * - `init` — Canvas loaded, hide loading spinner
 * - `redirectURL` — User clicked a template, navigate to designer page
 *
 * Matches scio1 `templateManagementComponent.js` event handling.
 */
export function useCanvasIframe({ canvasOrigin, onReady }: UseCanvasIframeOptions) {
  const navigate = useNavigate();
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Security: only accept messages from the Canvas origin (fail closed)
      if (!canvasOrigin || event.origin !== canvasOrigin) return;

      let data: CanvasMessage;
      if (typeof event.data === 'string') {
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
      } else if (typeof event.data === 'object' && event.data !== null) {
        data = event.data as CanvasMessage;
      } else {
        return;
      }

      // Canvas sends { event: 'init' } / { event: 'redirectURL' } (scio1 reads msg.event)
      const action = data.event ?? data.action;
      if (!action) return;

      logDebug(`received [${action}]`, { origin: event.origin, data });

      switch (action) {
        case 'init': {
          logDebug('Canvas iframe ready');
          onReadyRef.current();
          break;
        }

        case 'redirectURL': {
          // scio1: parses toUrl params and navigates to designManagement with templateManagement=1
          const toUrl = data.data?.toUrl as string | undefined;
          if (!toUrl) {
            logDebug('redirectURL: no toUrl provided');
            break;
          }

          logDebug('redirectURL: navigating to designer', { toUrl });

          // Parse query params from the Canvas redirect URL
          try {
            const url = new URL(toUrl, window.location.origin);
            const params = new URLSearchParams(url.search);
            // Always add templateManagement=1 so designer knows we came from templates
            params.set('templateManagement', '1');
            // Forward folder context so Designer saves to the originating folder.
            // Read from sessionStorage where usePersistedPath('assets') saves the
            // last-visited folder. Works regardless of how user navigated to Templates
            // (sidebar, dropdown, or top nav).
            // Only forward valid folder paths (start with /, not special modes like
            // SHARE_WITH_ME). If the folder was deleted, Designer will save to root
            // as fallback — acceptable UX vs blocking the navigation.
            try {
              const stored = sessionStorage.getItem(`${PERSISTED_PATH_PREFIX}assets`);
              if (stored) {
                const parsed: Record<string, string> = JSON.parse(stored);
                if (parsed.path && parsed.path.startsWith('/') && parsed.path !== '/') {
                  params.set('path', parsed.path);
                }
              }
            } catch {
              /* ignore parse errors */
            }
            navigate(`/app/designManagement?${params.toString()}`);
          } catch {
            // Fallback: append as-is
            logDebug('redirectURL: failed to parse URL, using raw params');
            navigate(`/app/designManagement?templateManagement=1&${toUrl}`);
          }
          break;
        }

        default:
          logDebug(`unhandled action: ${action}`, { data });
          break;
      }
    },
    [canvasOrigin, navigate],
  );

  useEffect(() => {
    logDebug('attaching postMessage listener', { canvasOrigin });
    window.addEventListener('message', handleMessage);
    return () => {
      logDebug('detaching postMessage listener');
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage, canvasOrigin]);
}
